import * as CSS from 'csstype';
import React from 'react';
import { useFocusScroll } from '../../../../../hooks/useFocusScroll';
import { NavigableElement } from '../../../CfDataGridProps';
import { ValueGetter } from '../CfDataGridTableViewProps';
import { CfDataGridCustomRow } from './CfDataGridCustomRow';
import { CfDataGridRowFooter } from './CfDataGridFooterRow';
import { CfDataGridSkeletonRow } from './CfDataGridSkeletonRow';
import { CfDataGridRowProps } from './common';
import { StyledGridCell, StyledGridRow } from './row.styles';

export function CfDataGridRows<TItem>(props: CfDataGridRowProps<TItem>): JSX.Element {
  const item = props.item;
  if (!item) {
    return <CfDataGridSkeletonRow tableViewDefinition={props.tableViewDefinition} itemIndex={props.itemIndex} />;
  }

  const virtualizedRow = props.tableViewDefinition.virtualizedProps?.enabled ?? false;
  const [elementRef, handleFocus] = useFocusScroll<HTMLTableRowElement>(0, virtualizedRow);

  let cursorStyle: CSS.Property.Cursor = 'default';
  if (props.onItemClicked) {
    cursorStyle = 'pointer';
  }

  if (props.cursorStyle !== undefined) {
    if (typeof props.cursorStyle === 'function') {
      cursorStyle = props.cursorStyle(item, 'table');
    } else {
      cursorStyle = props.cursorStyle;
    }
  }

  let hover = true;

  if (props.hoverStyle !== undefined) {
    if (typeof props.hoverStyle === 'function') {
      hover = props.hoverStyle(item, 'table');
    } else {
      hover = props.hoverStyle;
    }
  }

  const customRowBefore = props.tableViewDefinition.insertCustomRow?.(
    { item: props.previousItem, itemIndex: props.itemIndex - 1 },
    { item: props.item, itemIndex: props.itemIndex },
    undefined,
    undefined,
    (navigableElement: NavigableElement, columnIndex?: number) =>
      props.setNavigableElement(navigableElement, props.itemIndex, columnIndex ?? 0)
  );

  const customRowAfter =
    props.itemIndex === props.itemCount - 1
      ? props.tableViewDefinition.insertCustomRow?.(
          { item: props.item, itemIndex: props.itemIndex },
          { item: undefined, itemIndex: props.itemCount },
          undefined,
          undefined,
          (navigableElement: NavigableElement, columnIndex?: number) =>
            props.setNavigableElement(navigableElement, props.itemIndex, columnIndex ?? 0)
        )
      : undefined;

  const footerRow = props.tableViewDefinition.insertFooterRow?.({
    item: item,
    itemIndex: props.itemIndex,
    recomputeRowHeights: undefined,
    recomputeRowHeight: undefined,
    setNavigableElement: (navigableElement: NavigableElement, columnIndex?: number) =>
      props.setNavigableElement(navigableElement, props.itemIndex, columnIndex ?? 0),
  });

  const bodyTableRowProps =
    typeof props.tableViewDefinition.bodyTableRowProps === 'function'
      ? props.tableViewDefinition.bodyTableRowProps(props.item)
      : props.tableViewDefinition.bodyTableRowProps;

  const augmentedRowSx = { ...bodyTableRowProps?.sx, ...{ cursor: cursorStyle } };

  return (
    <>
      {customRowBefore && (
        <CfDataGridCustomRow content={customRowBefore.content} tableRowSxProps={customRowBefore.tableRowSxProps} />
      )}
      <StyledGridRow
        className='CfTableRow-row'
        ref={elementRef}
        onClick={() => props.onItemClicked?.(item, 'table', props.itemIndex)}
        onKeyDown={(event: React.KeyboardEvent<HTMLTableRowElement>) =>
          event.key === 'Enter' && props.onItemClicked?.(item, 'table', props.itemIndex)
        }
        //hover={hover}
        {...bodyTableRowProps}
        sx={augmentedRowSx}
        onFocus={handleFocus}
      >
        {props.valueGetters.map((getter: ValueGetter<TItem>, index: number) => {
          const bodyCellProps =
            typeof getter.column.bodyCellProps === 'function'
              ? getter.column.bodyCellProps(props.item, props.itemIndex)
              : getter.column.bodyCellProps;
          return (
            <StyledGridCell
              key={index}
              {...bodyCellProps}
              sx={{ textAlign: getter.column.horizontalAlignment, width: getter.column.width, ...bodyCellProps?.sx }}
            >
              {getter.getValue({
                item: item,
                itemIndex: props.itemIndex,
                filterText: props.filterText,
                setNavigableElement: (navigableElement: NavigableElement, columnIndex?: number) =>
                  props.setNavigableElement(navigableElement, props.itemIndex, columnIndex ?? index),
                recomputeRowHeights: undefined,
                recomputeRowHeight: undefined,
              })}
            </StyledGridCell>
          );
        })}
      </StyledGridRow>
      {footerRow && <CfDataGridRowFooter content={footerRow.content} tableRowSxProps={footerRow.tableRowSxProps} />}
      {customRowAfter && (
        <CfDataGridCustomRow content={customRowAfter.content} tableRowSxProps={customRowAfter.tableRowSxProps} />
      )}
    </>
  );
}
